import React, { useState, useEffect } from 'react';
import { FaTimes, FaBuilding, FaFileInvoice, FaPlus, FaTrash, FaUser, FaPhone, FaEnvelope } from 'react-icons/fa';
import { SearchableSelect, SelectWithSearch } from '../../utils/select-with-search';
import axiosInstance from '../../libs/axios';
import { countries } from '../../utils/countries';
import { LocationAutocomplete } from '../../utils/location-autocomplete';
import { CloseModal } from '../sites/closemodal';

interface ClientRepresentative {
  contact_person: string;
  contact_number: string;
  country_code: string;
  contact_number_with_code?: string;
  designation_id: number | '';
  representative_location: string;
  email?: string;
}

interface ClientFormData {
  client_full_name: string;
  client_short_name: string;
  address: string;
  gst_number: string;
  state_circle_id: number | '';
  representatives?: ClientRepresentative[];
}

interface AddClientOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading?: boolean;
}

export interface DesignationOption {
  value: number;
  label: string;
}

export interface CountryOption {
  value: string;
  label: string;
  flag: string;
}

const initialFormData: ClientFormData = {
  client_full_name: '',
  client_short_name: '',
  address: '',
  gst_number: '',
  state_circle_id: '',
  representatives: []
};

const initialRepresentative: ClientRepresentative = {
  contact_person: '',
  contact_number: '',
  country_code: '+91',
  designation_id: '',
  representative_location: '',
  email: ''
};

export const AddClientOffcanvas: React.FC<AddClientOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  const [representatives, setRepresentatives] = useState<ClientRepresentative[]>([]);
  const [errors, setErrors] = useState<Partial<ClientFormData>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [representativeErrors, setRepresentativeErrors] = useState<Record<number, Partial<ClientRepresentative>>>({});
  const [selectedStateCircle, setSelectedStateCircle] = useState<any | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Designation state
  const [designationOptions, setDesignationOptions] = useState<DesignationOption[]>([]);
  const [designationLoading, setDesignationLoading] = useState(false);
  const [designationPage, setDesignationPage] = useState(1);
  const [designationHasMore, setDesignationHasMore] = useState(true);
  const [designationSearch, setDesignationSearch] = useState("");

  // Country code options
  const countryOptions: CountryOption[] = countries.map(country => ({
    value: country.code,
    label: `${country.flag} ${country.code}`,
    flag: country.flag
  }));

  // Fetch designations with pagination and search
  const fetchDesignations = async (search: string = "", page: number = 1, append: boolean = false) => {
    setDesignationLoading(true);
    try {
      const response = await axiosInstance.get('/designations/list', {
        params: {
          search: search.trim() || undefined,
          page,
          limit: 20,
          status: 'active'
        }
      });

      if (response.data.success) {
        const newDesignations = response.data.data.designations.map((d: any) => ({
          value: d.id,
          label: d.name
        }));

        if (append) {
          setDesignationOptions(prev => [...prev, ...newDesignations]);
        } else {
          setDesignationOptions(newDesignations);
        }

        setDesignationHasMore(response.data.data.pagination.has_next_page);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    } finally {
      setDesignationLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setShowCloseModal(false);
    }
  }, [isOpen]);

  // Initial load of designations
  useEffect(() => {
    if (isOpen) {
      fetchDesignations();
    }
  }, [isOpen]);

  // Handle designation search
  const handleDesignationSearch = (search: string) => {
    setDesignationSearch(search);
    setDesignationPage(1);
    fetchDesignations(search, 1, false);
  };

  // Load more designations
  const loadMoreDesignations = () => {
    if (designationHasMore && !designationLoading) {
      const nextPage = designationPage + 1;
      setDesignationPage(nextPage);
      fetchDesignations(designationSearch, nextPage, true);
    }
  };

  // Handle state circle selection
  const handleStateCircleChange = (option: any | null) => {
    setSelectedStateCircle(option);
    setFormData((prev) => ({
      ...prev,
      state_circle_id: option ? Number(option.value) : '',
    }));
    if (errors.state_circle_id) {
      setErrors((prev) => ({
        ...prev,
        state_circle_id: undefined,
      }));
    }
  };

  // Validation
  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'client_full_name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Full name must be at least 2 characters';
        if (value.trim().length > 100) return 'Full name must be less than 100 characters';
        break;
      case 'client_short_name':
        if (!value.trim()) return 'Short name is required';
        if (value.trim().length < 2) return 'Short name must be at least 2 characters';
        if (value.trim().length > 50) return 'Short name must be less than 50 characters';
        break;
      case 'address':
        if (!value.trim()) return 'Address is required';
        if (value.trim().length < 10) return 'Address must be at least 10 characters';
        if (value.trim().length > 500) return 'Address must be less than 500 characters';
        break;
      case 'gst_number':
        if (!value.trim()) return 'GST number is required';
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(value.trim())) return 'Invalid GST number format';
        break;
      case 'state_circle_id':
        if (value === '' || Number.isNaN(Number(value))) return 'State is required';
        break;
    }
    return null;
  };

  const validateRepresentative = (rep: ClientRepresentative) => {
    const repErrors: Partial<ClientRepresentative> = {};

    if (!rep.contact_person.trim()) {
      repErrors.contact_person = 'Contact person is required';
    } else if (rep.contact_person.trim().length < 2) {
      repErrors.contact_person = 'Name must be at least 2 characters';
    }

    if (!rep.contact_number.trim()) {
      repErrors.contact_number = 'Contact number is required';
    } else if (rep.contact_number.trim().length < 6 || rep.contact_number.trim().length > 15) {
      repErrors.contact_number = 'Enter a valid phone number';
    }

    if (rep.designation_id === '' || rep.designation_id === null) {
      repErrors.designation_id = 'Designation is required' as any;
    }

    if (!rep.representative_location.trim()) {
      repErrors.representative_location = 'Location is required';
    }

    if (rep.email && rep.email.trim() && !/^\S+@\S+\.\S+$/.test(rep.email.trim())) {
      repErrors.email = 'Enter a valid email';
    }

    return repErrors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: name === 'state_circle_id' ? (value ? Number(value) : '') : value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ClientFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Validate field if touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Representative handlers
  const addRepresentative = () => {
    setRepresentatives(prev => [...prev, { ...initialRepresentative }]);
  };

  const removeRepresentative = (index: number) => {
    setRepresentatives(prev => prev.filter((_, i) => i !== index));
    setRepresentativeErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  const updateRepresentative = (index: number, field: keyof ClientRepresentative, value: string | number) => {
    setRepresentatives(prev =>
      prev.map((rep, i) => i === index ? { ...rep, [field]: value } : rep)
    );

    // Clear error when user starts typing
    if (representativeErrors[index]?.[field]) {
      setRepresentativeErrors(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: undefined
        }
      }));
    }
  };

  const updateRepresentativeCountryCode = (index: number, option: CountryOption | null) => {
    if (option) {
      updateRepresentative(index, "country_code", option.value);
    }
  };

  const updateRepresentativeDesignation = (index: number, option: DesignationOption | null) => {
    updateRepresentative(index, "designation_id", option ? option.value : "");
  };

  const validateRepresentatives = () => {
    const errors: Record<number, Partial<ClientRepresentative>> = {};
    let hasErrors = false;

    representatives.forEach((rep, index) => {
      const repErrors = validateRepresentative(rep);
      if (Object.keys(repErrors).length > 0) {
        errors[index] = repErrors;
        hasErrors = true;
      }
    });

    setRepresentativeErrors(errors);
    return !hasErrors;
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    // Validate required fields
    const fieldsToValidate = ['client_full_name', 'client_short_name', 'address', 'gst_number', 'state_circle_id'];
    fieldsToValidate.forEach(field => {
      const value = formData[field as keyof ClientFormData];
      const error = validateField(field, String(value ?? ''));
      if (error) {
        newErrors[field as keyof ClientFormData] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched({
      client_full_name: true,
      client_short_name: true,
      address: true,
      gst_number: true,
      state_circle_id: true
    });

    // Validate representatives
    if (!validateRepresentatives()) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: ClientFormData = {
      ...formData,
      client_full_name: formData.client_full_name.trim(),
      client_short_name: formData.client_short_name.trim(),
      address: formData.address.trim(),
      gst_number: formData.gst_number.trim().toUpperCase(),
      state_circle_id: Number(formData.state_circle_id),
      representatives: representatives.length > 0 ? representatives.map(rep => ({
        ...rep,
        contact_person: rep.contact_person.trim(),
        contact_number: rep.contact_number.trim(),
        country_code: rep.country_code,
        designation_id: Number(rep.designation_id),
        representative_location: rep.representative_location.trim(),
        email: rep.email?.trim() || undefined,
      })) : []
    };

    onSubmit(submitData);
  };

  const requestClose = () => {
    if (!loading) {
      setShowCloseModal(true);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setShowCloseModal(false);
      // Reset form
      setFormData(initialFormData);
      setRepresentatives([]);
      setErrors({});
      setTouched({});
      setRepresentativeErrors({});
      setSelectedStateCircle(null);
      setDesignationOptions([]);
      setDesignationPage(1);
      setDesignationSearch("");
      onClose();
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        setShowCloseModal(true);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={requestClose} />

      {/* Offcanvas */}
      <div className="overflow-y-auto fixed right-0 top-0 h-full w-full sm:max-w-4xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add New Client</h2>
            <p className="text-sm text-gray-600 mt-1">Create a new client record with representative details</p>
          </div>
          <button
            onClick={requestClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-32">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Client Basic Information */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <FaBuilding className="w-5 h-5 text-blue-800" />
                Client Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="client_full_name"
                    value={formData.client_full_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter client full name"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.client_full_name && touched.client_full_name
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                      }`}
                    disabled={loading}
                  />
                  {errors.client_full_name && touched.client_full_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.client_full_name}</p>
                  )}
                </div>

                {/* Short Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="client_short_name"
                    value={formData.client_short_name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter client short name"
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${errors.client_short_name && touched.client_short_name
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                      }`}
                    disabled={loading}
                  />
                  {errors.client_short_name && touched.client_short_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.client_short_name}</p>
                  )}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter complete address"
                    rows={3}
                    className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none ${errors.address && touched.address
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                      }`}
                    disabled={loading}
                  />
                  {errors.address && touched.address && (
                    <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                  )}
                </div>

                {/* GST Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaFileInvoice className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="gst_number"
                      value={formData.gst_number}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="e.g. 27AAAAA0000A1Z5"
                      className={`w-full pl-10 pr-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono ${errors.gst_number && touched.gst_number
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                        }`}
                      disabled={loading}
                      maxLength={15}
                    />
                  </div>
                  {errors.gst_number && touched.gst_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.gst_number}</p>
                  )}
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State Circle <span className="text-red-500">*</span>
                  </label>
                  <SelectWithSearch
                    value={selectedStateCircle}
                    onChange={handleStateCircleChange}
                    placeholder="Select state"
                    apiEndpoint="/state-circle/list"
                    dataKey="states"
                    labelKey="state_name"
                    displayFormat={(item) => `${item.state_name}`}
                  />
                  {errors.state_circle_id && touched.state_circle_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.state_circle_id}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Representatives Section */}
            <div className="bg-gray-50 border border-gray-200 rounded p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <FaUser className="w-5 h-5 text-blue-800" />
                  Client Representatives
                  <span className="text-sm font-normal text-gray-600">
                    ({representatives.length})
                  </span>
                </h3>
                <button
                  type="button"
                  onClick={addRepresentative}
                  disabled={loading}
                  className="bg-blue-800 hover:bg-blue-900 text-white px-3 py-2 rounded text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaPlus className="w-3 h-3" />
                  Add Representative
                </button>
              </div>

              {representatives.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No representatives added yet</p>
                  <p className="text-xs mt-1">Click "Add Representative" to add client contacts</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {representatives.map((rep, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-gray-800">
                          Representative #{index + 1}
                        </h4>
                        <button
                          type="button"
                          onClick={() => removeRepresentative(index)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Contact Person */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Contact Person <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaUser className="w-3 h-3 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              value={rep.contact_person}
                              onChange={(e) => updateRepresentative(index, 'contact_person', e.target.value)}
                              placeholder="Enter contact person name"
                              className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_person
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300'
                                }`}
                              disabled={loading}
                            />
                          </div>
                          {representativeErrors[index]?.contact_person && (
                            <p className="mt-1 text-xs text-red-600">
                              {representativeErrors[index].contact_person}
                            </p>
                          )}
                        </div>

                        {/* Contact Number */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Contact Number <span className="text-red-500">*</span>
                          </label>
                          <div className="flex gap-2">
                            <div className="w-32">
                              <SearchableSelect
                                value={countryOptions.find(c => c.value === rep.country_code) || null}
                                onChange={(option: any) => updateRepresentativeCountryCode(index, option)}
                                options={countryOptions}
                                placeholder="Code"
                                disabled={loading}
                              />
                            </div>
                            <div className="flex-1 relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaPhone className="w-3 h-3 text-gray-400" />
                              </div>
                              <input
                                type="tel"
                                value={rep.contact_number}
                                onChange={(e) => updateRepresentative(index, 'contact_number', e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="Enter contact number"
                                className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.contact_number
                                  ? 'border-red-500 bg-red-50'
                                  : 'border-gray-300'
                                  }`}
                                disabled={loading}
                              />
                            </div>
                          </div>
                          {representativeErrors[index]?.contact_number && (
                            <p className="mt-1 text-xs text-red-600">
                              {representativeErrors[index].contact_number}
                            </p>
                          )}
                        </div>

                        {/* Email */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaEnvelope className="w-3 h-3 text-gray-400" />
                            </div>
                            <input
                              type="email"
                              value={rep.email ?? ""}
                              onChange={(e) => updateRepresentative(index, "email", e.target.value)}
                              placeholder="Enter email (optional)"
                              className={`w-full pl-9 pr-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${representativeErrors[index]?.email ? "border-red-500 bg-red-50" : "border-gray-300"
                                }`}
                              disabled={loading}
                            />
                          </div>
                          {representativeErrors[index]?.email && (
                            <p className="mt-1 text-xs text-red-600">{representativeErrors[index]?.email}</p>
                          )}
                        </div>

                        {/* Designation */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Designation <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            value={designationOptions.find(d => d.value === rep.designation_id) || null}
                            onChange={(option: any) => updateRepresentativeDesignation(index, option)}
                            options={designationOptions}
                            placeholder="Select designation"
                            disabled={loading}
                            isLoading={designationLoading}
                            onSearch={handleDesignationSearch}
                            error={representativeErrors[index]?.designation_id as string}
                          />
                        </div>

                        {/* Location */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Location <span className="text-red-500">*</span>
                          </label>
                          <LocationAutocomplete
                            value={rep.representative_location}
                            onChange={(value) => updateRepresentative(index, "representative_location", value)}
                            placeholder="Search and select location"
                            disabled={loading}
                            error={representativeErrors[index]?.representative_location}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={requestClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading || !formData.client_full_name.trim() || !formData.client_short_name.trim() || formData.state_circle_id === ''}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-900 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                <>
                  <FaBuilding className="w-4 h-4" />
                  Create Client
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={handleClose}
      />
    </>
  );
};